'use client';

import type {
  MysteryRoomIncidentSourceType,
  MysteryRoomIncidentKind,
  MysteryRoomIncidentState,
  MysteryRoomLocalRuntime,
  MysteryRoomNpcRoleAction,
  MysteryRoomWorldAlarmState,
} from '../core/localRuntime';

export type UpsertIncidentInput = {
  kind: MysteryRoomIncidentKind;
  zoneId: string;
  position: {
    x: number;
    y: number;
  };
  severity: number;
};

export type UpsertIncidentFromAnchorInput = {
  anchorId: string;
  sourceType: MysteryRoomIncidentSourceType;
  sourceActorId: 'system' | 'hero' | string;
  severity: number;
};

const WORLD_ALARM_TTL_MS = 2_500;
const ANCHOR_RETRIGGER_COOLDOWN_MS = 2_000;
const SYSTEM_SEED_RULES = [
  { anchorId: 'server_fault_main', everyMs: 5_000, severity: 0.58 },
  { anchorId: 'door_failure_server', everyMs: 8_000, severity: 0.54 },
] as const;

export function upsertIncident(runtime: MysteryRoomLocalRuntime, input: UpsertIncidentInput): MysteryRoomLocalRuntime {
  const id = `${input.kind}_${Math.round(input.position.x)}_${Math.round(input.position.y)}`;
  const existingIncident = runtime.incidents.find((incident) => incident.id === id);
  const incidentDefaults = {
    sourceType: 'system' as const,
    sourceActorId: 'system' as const,
    resolvedAtMs: null,
    resolutionAction: null,
    resolutionRemainingMs: 0,
  };
  const nextIncident: MysteryRoomIncidentState = !existingIncident
    ? {
        id,
        kind: input.kind,
        zoneId: input.zoneId,
        position: { ...input.position },
        severity: input.severity,
        ...incidentDefaults,
        status: 'active',
        assignedNpcId: null,
        leaseUntilMs: 0,
        createdAtMs: runtime.elapsedMs,
      }
    : existingIncident.status === 'resolved'
      ? {
          id,
        kind: input.kind,
        zoneId: input.zoneId,
        position: { ...input.position },
        severity: input.severity,
        ...incidentDefaults,
        status: 'active',
        assignedNpcId: null,
        leaseUntilMs: 0,
        createdAtMs: runtime.elapsedMs,
      }
    : {
          ...existingIncident,
          kind: input.kind,
          zoneId: input.zoneId,
          position: { ...input.position },
          severity: input.severity,
          sourceType: existingIncident.sourceType ?? incidentDefaults.sourceType,
          sourceActorId: existingIncident.sourceActorId ?? incidentDefaults.sourceActorId,
          resolvedAtMs: existingIncident.resolvedAtMs ?? incidentDefaults.resolvedAtMs,
          resolutionAction: existingIncident.resolutionAction ?? incidentDefaults.resolutionAction,
          resolutionRemainingMs: existingIncident.resolutionRemainingMs ?? incidentDefaults.resolutionRemainingMs,
        };
  const incidents = runtime.incidents.filter((incident) => incident.id !== id);

  incidents.push(nextIncident);

  return {
    ...runtime,
    incidents,
  };
}

export function upsertIncidentFromAnchor(
  runtime: MysteryRoomLocalRuntime,
  input: UpsertIncidentFromAnchorInput,
): MysteryRoomLocalRuntime {
  const anchor = runtime.map.incidentAnchors.find((entry) => entry.id === input.anchorId);
  if (!anchor) {
    return runtime;
  }

  const existingIncident = runtime.incidents.find((incident) => incident.id === anchor.id);
  const nextIncident = buildAnchorIncident(runtime, anchor.id, anchor.kind, anchor.zoneId, anchor.position, input, existingIncident);

  return {
    ...runtime,
    incidents: [...runtime.incidents.filter((incident) => incident.id !== anchor.id), nextIncident],
  };
}

export function seedSystemIncidents(runtime: MysteryRoomLocalRuntime, dtMs: number): MysteryRoomLocalRuntime {
  return SYSTEM_SEED_RULES.reduce((currentRuntime, rule) => {
    const crossedWindow =
      Math.floor(currentRuntime.elapsedMs / rule.everyMs) !==
      Math.floor((currentRuntime.elapsedMs + dtMs) / rule.everyMs);

    if (!crossedWindow) {
      return currentRuntime;
    }

    const existingIncident = currentRuntime.incidents.find(
      (incident) => incident.id === rule.anchorId && incident.status !== 'resolved',
    );

    if (existingIncident) {
      return currentRuntime;
    }

    return upsertIncidentFromAnchor(currentRuntime, {
      anchorId: rule.anchorId,
      sourceType: 'system',
      sourceActorId: 'system',
      severity: rule.severity,
    });
  }, runtime);
}

export function beginIncidentResolution(
  incident: MysteryRoomIncidentState,
  npcId: string,
  action: MysteryRoomNpcRoleAction,
  resolutionRemainingMs: number,
): MysteryRoomIncidentState {
  if (
    incident.status === 'resolved' ||
    (incident.assignedNpcId !== null && incident.assignedNpcId !== npcId)
  ) {
    return incident;
  }

  return {
    ...incident,
    assignedNpcId: npcId,
    status: 'resolving',
    resolutionAction: action,
    resolutionRemainingMs,
  };
}

export function advanceIncidentResolution(
  incident: MysteryRoomIncidentState,
  dtMs: number,
  nowMs: number,
): MysteryRoomIncidentState {
  if (incident.status !== 'resolving') {
    return incident;
  }

  const resolutionRemainingMs = Math.max(0, incident.resolutionRemainingMs - dtMs);
  if (resolutionRemainingMs > 0) {
    return {
      ...incident,
      resolutionRemainingMs,
    };
  }

  if (incident.kind === 'alarm_call') {
    return {
      ...incident,
      kind: 'alarm',
      severity: 1,
      status: 'active',
      assignedNpcId: null,
      leaseUntilMs: 0,
      resolvedAtMs: null,
      resolutionAction: null,
      resolutionRemainingMs: 0,
    };
  }

  return {
    ...incident,
    status: 'resolved',
    resolutionRemainingMs: 0,
    resolvedAtMs: nowMs,
  };
}

export function updateWorldAlarmFromIncidents(
  worldAlarm: MysteryRoomWorldAlarmState,
  incidents: MysteryRoomIncidentState[],
  nowMs: number,
): MysteryRoomWorldAlarmState {
  const criticalIncident = incidents
    .filter((incident) => isAlarmTriggeringIncident(incident))
    .sort(compareAlarmTriggerPriority)[0];

  if (criticalIncident) {
    return {
      state: 'active',
      sourceIncidentId: criticalIncident.id,
      activeUntilMs: nowMs + WORLD_ALARM_TTL_MS,
    };
  }

  if (worldAlarm.state === 'active' && worldAlarm.activeUntilMs > nowMs) {
    return worldAlarm;
  }

  return {
    state: 'clear',
    sourceIncidentId: null,
    activeUntilMs: 0,
  };
}

export function assignIncidentToNpc(
  incident: MysteryRoomIncidentState,
  npcId: string,
  leaseUntilMs: number,
  options: {
    nowMs?: number;
    override?: boolean;
  } = {},
): MysteryRoomIncidentState {
  if (incident.status === 'resolved') {
    return incident;
  }

  const requiresCompetingLeaseCheck =
    !options.override &&
    incident.assignedNpcId !== null &&
    incident.assignedNpcId !== npcId &&
    (incident.status === 'assigned' || incident.status === 'resolving');

  if (requiresCompetingLeaseCheck && options.nowMs === undefined) {
    throw new Error('assignIncidentToNpc requires nowMs for competing lease checks');
  }

  const nowMs = options.nowMs ?? 0;

  if (
    requiresCompetingLeaseCheck &&
    incident.leaseUntilMs > nowMs
  ) {
    return incident;
  }

  return {
    ...incident,
    assignedNpcId: npcId,
    status: 'assigned',
    leaseUntilMs,
  };
}

export function assignIncidentIfOwnedByTask(
  incidents: MysteryRoomIncidentState[],
  npc: MysteryRoomLocalRuntime['npcs'][number],
  nowMs: number,
) {
  if (!npc.currentTask?.assignedIncidentId) {
    return incidents;
  }

  return incidents.map((incident) =>
    incident.id === npc.currentTask!.assignedIncidentId && incident.status === 'active'
      ? assignIncidentToNpc(incident, npc.id, nowMs + npc.currentTask!.minHoldMs, { nowMs })
      : incident,
  );
}

export function advanceAllIncidentResolutions(
  incidents: MysteryRoomIncidentState[],
  dtMs: number,
  nowMs: number,
) {
  return incidents.map((incident) => advanceIncidentResolution(incident, dtMs, nowMs));
}

function isAlarmTriggeringIncident(incident: MysteryRoomIncidentState) {
  if (incident.status === 'resolved') {
    return false;
  }

  if (incident.kind === 'alarm') {
    return true;
  }

  return (incident.kind === 'fire' || incident.kind === 'reactor_fault') && incident.severity >= 0.9;
}

function compareAlarmTriggerPriority(a: MysteryRoomIncidentState, b: MysteryRoomIncidentState) {
  return (
    b.severity - a.severity ||
    a.createdAtMs - b.createdAtMs ||
    a.id.localeCompare(b.id)
  );
}

function buildAnchorIncident(
  runtime: MysteryRoomLocalRuntime,
  id: string,
  kind: MysteryRoomIncidentKind,
  zoneId: string,
  position: { x: number; y: number },
  input: UpsertIncidentFromAnchorInput,
  existingIncident: MysteryRoomIncidentState | undefined,
): MysteryRoomIncidentState {
  const baseIncident = {
    id,
    kind,
    zoneId,
    position: { ...position },
    sourceType: input.sourceType,
    sourceActorId: input.sourceActorId,
    severity: input.severity,
    status: 'active' as const,
    assignedNpcId: null,
    leaseUntilMs: 0,
    createdAtMs: runtime.elapsedMs,
    resolvedAtMs: null,
    resolutionAction: null,
    resolutionRemainingMs: 0,
  };

  if (!existingIncident) {
    return baseIncident;
  }

  if (existingIncident.status === 'resolved') {
    if (
      existingIncident.resolvedAtMs !== null &&
      runtime.elapsedMs - existingIncident.resolvedAtMs < ANCHOR_RETRIGGER_COOLDOWN_MS
    ) {
      return existingIncident;
    }

    return baseIncident;
  }

  if (existingIncident.status === 'assigned' || existingIncident.status === 'resolving') {
    return {
      ...existingIncident,
      kind,
      zoneId,
      position: { ...position },
      sourceType: input.sourceType,
      sourceActorId: input.sourceActorId,
      severity: input.severity,
    };
  }

  return {
    ...existingIncident,
    kind,
    zoneId,
    position: { ...position },
    sourceType: input.sourceType,
    sourceActorId: input.sourceActorId,
    severity: input.severity,
    resolvedAtMs: null,
    resolutionAction: null,
    resolutionRemainingMs: 0,
  };
}
